import { FC } from 'react';

import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { ResaleListingGroup } from '../../hooks/contexts/resale-listing.context';
import formatNumericValue, { formatNumericAmount } from '../../utils/formatNumericValue';
import { ResaleFeeInfoButton } from '../ResaleFeeInfoButton';
import { ResalePriceBadge } from '../ResalePriceBadge';
import * as Style from './styles';

type ResaleListingItemProps = {
  row: ResaleListingGroup;
  /** Out of the Tab order while the accordion is closed. */
  focusable: boolean;
  onSelect: (group: ResaleListingGroup) => void;
  /** Opens the "Entenda as taxas" modal; receives the icon so focus returns to it. */
  onOpenFeesInfo: (trigger: HTMLButtonElement) => void;
};

const formatBrl = (value: number) => formatNumericValue({ value, discount : 0, currencyFormat : 'BRL' });

/**
 * Ticket type row in the resale accordion (Figma 16677-57204).
 *
 * Left: name, "N ofertas", resale price, "+ X de taxa" and the info icon.
 * Right: comparison badge against the original price, and "total".
 *
 * Fee, total and comparison come READY from the backend — the frontend never
 * computes money. Without fee and total (legacy backend or invalid value) the
 * row shows only the price; without comparison, there is no badge.
 *
 * Ticket selection is a `<button>` wrapping the name, labelled by name + price
 * and described by fee, total and badge (when present), whose `::after` covers
 * the whole row — clicking anywhere on it selects. The info icon is a SIBLING
 * of that button (stacked above the `::after`), not a child: a button nested in
 * another (or in a `role="button"`) is lost to screen readers. Enter/Space are
 * the button's native ones.
 */
const ResaleListingItem: FC<ResaleListingItemProps> = ({ row, focusable, onSelect, onOpenFeesInfo }) => {
  const { translate } = useI18nContext();

  const countLabel = translate(
    ['RESALE_LISTING', row.count === 1 ? 'OFFERS_COUNT' : 'OFFERS_COUNT_PLURAL'],
    { count : row.count }
  );
  const breakdown = row.serviceFee !== undefined && row.totalPrice !== undefined
    ? { fee : row.serviceFee, total : row.totalPrice }
    : null;
  // Fee, total and badge (transparency required by Procon) also reach screen reader
  // users tabbing through: they become the selection button's description, only when present.
  const describedBy = [
    breakdown && `resale-listing-item-fee-${row.uuid}`,
    breakdown && `resale-listing-item-total-${row.uuid}`,
    row.priceComparison && `resale-listing-item-badge-${row.uuid}`,
  ].filter(Boolean).join(' ') || undefined;

  return (
    <Style.Item
      id={`resale-listing-item-${row.uuid}`}
      $hasBadge={Boolean(row.priceComparison)}
      $hasTotal={breakdown !== null}
    >
      <Style.ItemInfo>
        <Style.SelectButton
          type="button"
          id={`resale-listing-item-select-${row.uuid}`}
          tabIndex={focusable ? undefined : -1}
          aria-labelledby={`resale-listing-item-name-${row.uuid} resale-listing-item-price-${row.uuid}`}
          aria-describedby={describedBy}
          onClick={() => onSelect(row)}
        >
          <Style.ItemName id={`resale-listing-item-name-${row.uuid}`}>
            {row.ticketName}
          </Style.ItemName>
        </Style.SelectButton>
        <Style.ItemCount id={`resale-listing-item-count-${row.uuid}`}>
          {countLabel}
        </Style.ItemCount>
      </Style.ItemInfo>

      <Style.PriceLine>
        <Style.ItemPrice id={`resale-listing-item-price-${row.uuid}`} translate="no">
          {formatBrl(row.finalPrice)}
        </Style.ItemPrice>
        {breakdown && (
          // Fee and icon together: if space runs out, they wrap as a single block.
          <Style.FeeGroup>
            <Style.ItemFee id={`resale-listing-item-fee-${row.uuid}`}>
              {translate(['RESALE_LISTING', 'SERVICE_FEE'], {
                amount : formatNumericAmount({ value : breakdown.fee, currencyFormat : 'BRL' }),
              })}
            </Style.ItemFee>
            <ResaleFeeInfoButton id={`resale-listing-item-fee-info-${row.uuid}`} onOpen={onOpenFeesInfo} />
          </Style.FeeGroup>
        )}
      </Style.PriceLine>

      <Style.ItemSummary>
        <Style.ItemBadge>
          <ResalePriceBadge comparison={row.priceComparison} id={`resale-listing-item-badge-${row.uuid}`} />
        </Style.ItemBadge>
        {breakdown && (
          <Style.ItemTotal id={`resale-listing-item-total-${row.uuid}`}>
            <Style.ItemTotalLabel>{translate(['RESALE_LISTING', 'TOTAL'])}</Style.ItemTotalLabel>
            {' '}
            <Style.ItemTotalValue translate="no">{formatBrl(breakdown.total)}</Style.ItemTotalValue>
          </Style.ItemTotal>
        )}
      </Style.ItemSummary>
    </Style.Item>
  );
};

export { ResaleListingItem };
